'use client';

import * as React from 'react';

import { useAnalytics } from '@workspace/analytics/hooks/use-analytics';
import {
  APP_DISCORD_INVITE_URL,
  APP_GITHUB_REPO_URL,
  APP_INSTALL_SCRIPT_URL
} from '@workspace/common/app';
import { routes } from '@workspace/routes';

type MarketingEventName =
  | 'install_click'
  | 'github_click'
  | 'docs_click'
  | 'newsletter_subscribe'
  | 'newsletter_subscribe_failed'
  | 'contact_submit'
  | 'contact_submit_failed'
  | 'discord_click';

type MarketingEventProperties = Record<string, string | number | boolean>;

function useTrackMarketingEvent(): (
  eventName: MarketingEventName,
  properties: MarketingEventProperties
) => void {
  const analytics = useAnalytics();

  return React.useCallback(
    (eventName: MarketingEventName, properties: MarketingEventProperties) => {
      void analytics.trackEvent(eventName, properties);
    },
    [analytics]
  );
}

export function useMarketingAnalytics() {
  const trackMarketingEvent = useTrackMarketingEvent();

  return React.useMemo(
    () => ({
      trackInstallClick(
        ctaLocation: string,
        actionKind: 'navigate' | 'copy_command' = 'navigate',
        destinationUrl = APP_INSTALL_SCRIPT_URL
      ): void {
        trackMarketingEvent('install_click', {
          cta_label: 'install',
          cta_location: ctaLocation,
          action_kind: actionKind,
          destination_url: destinationUrl
        });
      },
      trackGithubClick(ctaLocation: string): void {
        trackMarketingEvent('github_click', {
          cta_label: 'github',
          cta_location: ctaLocation,
          destination_url: APP_GITHUB_REPO_URL
        });
      },
      trackDocsClick(
        ctaLocation: string,
        destinationUrl: string = routes.marketing.Docs
      ): void {
        trackMarketingEvent('docs_click', {
          cta_label: 'docs',
          cta_location: ctaLocation,
          destination_url: destinationUrl
        });
      },
      trackNewsletterSubscribe(formLocation: string): void {
        trackMarketingEvent('newsletter_subscribe', {
          form_location: formLocation
        });
      },
      trackNewsletterSubscribeFailed(formLocation: string): void {
        trackMarketingEvent('newsletter_subscribe_failed', {
          form_location: formLocation
        });
      },
      trackContactSubmit(formLocation: string): void {
        trackMarketingEvent('contact_submit', {
          form_location: formLocation
        });
      },
      trackContactSubmitFailed(formLocation: string): void {
        trackMarketingEvent('contact_submit_failed', {
          form_location: formLocation
        });
      },
      trackDiscordClick(ctaLocation: string): void {
        trackMarketingEvent('discord_click', {
          cta_label: 'discord',
          cta_location: ctaLocation,
          destination_url: APP_DISCORD_INVITE_URL
        });
      }
    }),
    [trackMarketingEvent]
  );
}
